import {AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {appointmentAssistanceTypeIcons, appointmentLinkVerificationCodes, appointmentNoticeCategoriesClass} from '@constants/appointment.constants';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {AppointmentRequest, AppointmentRequestSelector} from '@shared/models/appointment-request.model';
import {AppConfigService} from '@shared/services/app-config.service';
import {AppointmentService} from '@shared/services/appointment.service';
import {ExternalReferenceService, IdName, LoaderService} from '@core/src/public-api';
import * as moment from 'moment';
import {Subscription} from 'rxjs';
import {PermissionService} from '@shared/services/permission.service';
import {AppointmentNotice} from '@shared/models/appointment.model';

@Component({
  selector: 'si-appo-appointment-request-date',
  templateUrl: './appo-appointment-request-date.component.html',
  styleUrls: ['./appo-appointment-request-date.component.scss']
})
export class AppoAppointmentRequestDateComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() model = new AppointmentRequest();
  @Input() form: NgForm;
  @Input() formId = 'appoAppointmentRequestDateForm';
  @Input() currentStep: number;
  @Output() formChange = new EventEmitter<NgForm>();
  @Output() currentStepChange = new EventEmitter<number>();
  @Output() slotsUpdateChange = new EventEmitter<number>();
  @ViewChild('appoAppointmentRequestDateForm') appoAppointmentRequestDateForm: NgForm;
  formSubscription: Subscription;
  slotsList: AppointmentRequest[] = [];
  datesSameDayBool = true;
  displayNoAvailableDatesDialog = false;
  displayTryAgainDatesDialog = false;
  noAvailableDatesText: string;
  tryAgainDatesText: string;
  slotSelectorValues: AppointmentRequestSelector[] = [];
  displayExistingAccountDialog = false;
  appointmentAssistanceTypeIcons = appointmentAssistanceTypeIcons;
  hasSmsVerification: boolean;
  hasEmailVerification: boolean;
  showAppoRequestBackButton: boolean;
  showRequestAppointmentButton = false;
  appointmentNoticeCategoriesClass = appointmentNoticeCategoriesClass;
  appointmentScheduleAlerts: AppointmentNotice[] = [];
  private _slotsUpdate: number;

  constructor(
    private appointmentService: AppointmentService,
    private loaderService: LoaderService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigService: AppConfigService,
    private permissionService: PermissionService,
    private externalReferenceService: ExternalReferenceService
  ) {
  }

  get slotsUpdate() {
    return this._slotsUpdate;
  }

  @Input()
  set slotsUpdate(val) {
    this._slotsUpdate = val;
    if (val) {
      this.getAppointmentSlots();
    }
  }

  ngOnInit(): void {
    this.hasSmsVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.SMS;
    this.hasEmailVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.EMAIL;
  }

  ngAfterViewInit(): void {
    this.appoAppointmentRequestDateForm.control.markAsDirty();
    this.changeDetectorRef.detectChanges();
    this.setFormSubscription();
  }

  setFormSubscription(): void {
    this.formSubscription = this.appoAppointmentRequestDateForm.statusChanges.subscribe(() => {
      this.form = this.appoAppointmentRequestDateForm;
      this.formChange.emit(this.appoAppointmentRequestDateForm);
    });
  }

  onChangeTimePreferenceSelector(): void {
    this.getAppointmentSlots();
  }

  onChangeFromDateDatepicker(): void {
    this.getAppointmentSlots();
  }

  onShowSlotsSelector(): void {
    if (!this.slotsList.length) {
      this.getAppointmentSlots();
    }
  }

  async getAppointmentSlots(): Promise<void> {
    try {
      this.loaderService.start();
      this.slotsList = await this.appointmentService.getAppointmentSlots(
        this.model.service?.id || this.model.service,
        this.idNamesToIdsString(this.model.appointmentReason),
        this.model.collective?.id,
        this.model.fromDate,
        this.model.professional?.id || this.model.professional,
        this.model.center?.id || this.model.center,
        this.model.timePreference?.id
      );
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0060') {
        this.tryAgainDatesText = e.error.message;
        this.displayTryAgainDatesDialog = true;
      }

      if (e.error?.applicationCode === 'PCS0011' || e.error?.applicationCode === 'PCS0012') {
        if (this.model.proposalId || this.model.oldAppointmentId) {
          this.noAvailableDatesText = 'SI_SLOTS_NOT_FOUND_INFO_PROPOSAL';
          this.showAppoRequestBackButton = false;
        } else if (this.permissionService.hasPermission('procedure:appointment_create') && !this.externalReferenceService.isActive()) {
          this.noAvailableDatesText = 'SI_SLOTS_NOT_FOUND_INFO_REQUEST_APPOINTMENT';
          this.showRequestAppointmentButton = true;
          this.showAppoRequestBackButton = true;
        } else {
          this.noAvailableDatesText = 'SI_SLOTS_NOT_FOUND_INFO';
          this.showAppoRequestBackButton = true;
        }

        this.form?.control.markAsPristine();
        this.displayNoAvailableDatesDialog = true;
      }

      throw e;
    } finally {
      this.loaderService.stop();
      this.datesSameDayBool = this.datesSameDay(this.slotsList[0]?.startDate, this.model.fromDate);
      this.model.slotSelected = null;
      this.createSlotForSelector();
      this.getAppointmentScheduleAlerts();
    }
  }

  idNamesToIdsString(list: IdName[]): string {
    return list.map(idName => idName.id).join(',');
  }

  datesSameDay(firstDate: number | Date, secondDate: number | Date): boolean {
    const firstMoment = moment(firstDate);
    const secondMoment = moment(secondDate);
    return firstMoment.isSame(secondMoment, 'day');
  }

  getNextDaySlots(): void {
    this.model.fromDate = moment(this.slotsList[0]?.startDate).add(1, 'day').startOf('day').valueOf();
  }

  createSlotForSelector(): void {
    this.slotSelectorValues = [];
    this.slotsList.forEach((value, index) => {
      this.slotSelectorValues.push({id: index, name: this.getHours(value.startDate), appointmentRequest: value});
    });
    this.model.slotSelected = (this.slotSelectorValues?.length === 1) ? this.slotSelectorValues[0] : null;
  }

  getHours(value: number): string {
    return moment(value).format('LT');
  }

  onAcceptGoPreviousStep(): void {
    this.currentStepChange.emit(0);
  }

  selectDefaultLoaded(defaultValue: any, key: string): void {
    this.model[key] = defaultValue;
  }

  onChangeSlotSelected(): void {
    this.getAppointmentScheduleAlerts();
  }

  async getAppointmentScheduleAlerts(): Promise<void> {
    if (!this.model.center || !this.model.service || !this.model.slotSelected) {
      this.appointmentScheduleAlerts = [];
      return;
    }

    this.loaderService.start();
    try {
      this.appointmentScheduleAlerts = await this.appointmentService.getAppointmentAlerts(
        this.model.center?.id,
        this.model.service?.id,
        null,
        null,
        this.model.slotSelected.appointmentRequest?.schedule?.id
      )
    } finally {
      this.loaderService.stop();
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
    // We set the value of slotsUpdate to 0 to avoid making two calls when entering this component:
    // When entering the component, a request is made to fetch available slots.
    // If the request needs to be triggered again (when managed externally, from the parent component), the value of slotsUpdate is incremented, which causes the slots request to run again.
    // If the user goes from step 2 back to step 1 and then returns to step 2, the component ends up making two slot requests: one triggered internally by the component itself, and another because the value of slotsUpdate is greater than 0.
    // That’s why we reset the value of slotsUpdate to 0 when leaving step 2.
    this.slotsUpdateChange.emit(0);
  }
}
